import { PositionInfo } from "@/types";
import { handleKeyboardClick, sleep } from "@/utils";
import { Dialog } from "@headlessui/react";
import clsx from "clsx";
import { t } from "i18next";
import {
	JSX,
	ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";

interface MenuShellProps {
	children: ReactNode;
	className?: string;
	isClosingMenu: boolean;
	isModal: boolean;
	isTransformOriginDeclared?: boolean;
	onClose: () => void;
	shouldUseAlternativeOverflowPosition?: boolean;
	show: boolean;
	x: number;
	y: number;
}

function MenuShell({
	children,
	className,
	isClosingMenu,
	isModal,
	isTransformOriginDeclared,
	onClose,
	shouldUseAlternativeOverflowPosition,
	show,
	x,
	y,
}: MenuShellProps): JSX.Element {
	const [position, setPosition] = useState<PositionInfo | null>(null);
	const [transformOrigin, setTransformOrigin] = useState<string>("");

	const menuRef = useRef<HTMLDivElement>(null);

	const calculatePosition = useCallback(async (): Promise<void> => {
		await sleep(0); // avoid menuRef.current being null
		if (!show || position || !menuRef.current) {
			return;
		}
		const menuRect = menuRef.current.getBoundingClientRect();
		if (!menuRect) {
			return;
		}
		const { width, height } = menuRect;
		const windowWidth = window.innerWidth;
		const windowHeight = window.innerHeight;
		let transformOrigin = "";

		const isWidthOverflow = x + width + 10 > windowWidth;
		let left = x;
		if (isWidthOverflow) {
			if (shouldUseAlternativeOverflowPosition) {
				left = windowWidth - width - 10;
			} else {
				left = x - width;
			}
		}
		transformOrigin += isWidthOverflow ? "right" : "left";

		const isHeightOverflow = y + height + 10 > windowHeight;
		let top = isHeightOverflow ? y - height : y;
		transformOrigin += isHeightOverflow ? " bottom" : " top";

		// Ensure left and top are not less than 10px
		left = Math.max(left, 10);
		top = Math.max(top, 10);

		setPosition({
			left: left,
			top: top,
		});
		setTransformOrigin(transformOrigin);
	}, [position, shouldUseAlternativeOverflowPosition, show, x, y]);

	useEffect(() => {
		void calculatePosition();
	}, [calculatePosition]);

	useEffect(() => {
		if (!show) {
			setPosition(null);
			setTransformOrigin("");
		}
	}, [show]);

	const menuContainer = (
		<>
			<div
				aria-label={t("close")}
				className={clsx("overlay", "z-index")}
				onClick={onClose}
				onKeyDown={handleKeyboardClick(onClose)}
				role="button"
				tabIndex={0}
			></div>
			<div
				className={clsx(
					className,
					!isClosingMenu && !!position && "fade-in-scale",
					isClosingMenu && "fade-out-scale",
				)}
				ref={menuRef}
				style={{
					left: position?.left || x,
					top: position?.top || y,
					transformOrigin: isTransformOriginDeclared
						? undefined
						: transformOrigin,
				}}
			>
				{children}
			</div>
		</>
	);
	return (
		<>
			{show && !isModal && menuContainer}
			<Dialog
				onClose={onClose}
				open={isModal && show}
			>
				{menuContainer}
			</Dialog>
		</>
	);
}

export default MenuShell;
